import { useCallback, useEffect, useState } from "react";
import Pagination from "@/components/base/Pagination";
import { api, resolveAssetUrl } from "@/lib/api";
import { buildListQuery, type PageResponse } from "@/lib/pagination";
import { useServerPagination } from "@/lib/useServerPagination";
import type { ContentFile } from "@/lib/types";

type MediaLibraryModalProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string, meta?: { originalFilename?: string | null }) => void;
  category?: "image" | "pdf";
  title?: string;
};

const GRID_PAGE_SIZE = 24;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function displayName(file: ContentFile): string {
  if (file.originalFilename?.trim()) return file.originalFilename.trim();
  const parts = file.url.split("/");
  return parts[parts.length - 1] || file.url;
}

function fallbackLabel(file: ContentFile): string {
  const parts = file.url.split("/");
  return parts[parts.length - 1] || file.url;
}

type GridItemProps = {
  file: ContentFile;
  onSelect: (file: ContentFile) => void;
  onRenamed: () => void;
};

function MediaLibraryGridItem({ file, onSelect, onRenamed }: GridItemProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(file.originalFilename?.trim() ?? "");
    setEditing(true);
  };

  const cancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(false);
    setEditValue("");
  };

  const saveFilename = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const trimmed = editValue.trim();
    if (!trimmed) {
      alert("검색용 파일명을 입력해주세요.");
      return;
    }
    setSaving(true);
    try {
      await api.patch<ContentFile>(`/admin/files/${file.id}/filename`, {
        originalFilename: trimmed,
      });
      setEditing(false);
      onRenamed();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "파일명 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden bg-white hover:border-[#1977cc]/50 hover:shadow-sm transition-all">
      <button
        type="button"
        onClick={() => onSelect(file)}
        className="w-full aspect-square bg-[#f6f9fc] flex items-center justify-center overflow-hidden cursor-pointer"
      >
        {file.fileCategory === "pdf" ? (
          <i className="ri-file-pdf-2-line text-4xl text-red-500" />
        ) : (
          <img
            src={resolveAssetUrl(file.url)}
            alt={displayName(file)}
            className="w-full h-full object-contain p-2"
            loading="lazy"
          />
        )}
      </button>

      <div className="px-2 py-2 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
        {editing ? (
          <form onSubmit={saveFilename} className="space-y-1.5">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              maxLength={255}
              autoFocus
              disabled={saving}
              placeholder="검색용 파일명"
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20"
            />
            <div className="flex gap-1">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-2 py-1 text-[10px] font-medium text-white bg-[#1977cc] rounded hover:bg-[#1669b3] disabled:opacity-50 cursor-pointer"
              >
                {saving ? "저장 중..." : "저장"}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="px-2 py-1 text-[10px] font-medium text-gray-600 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer"
              >
                취소
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-start gap-1 min-w-0">
              <p
                className="flex-1 text-xs font-medium text-[#2c4964] truncate"
                title={displayName(file)}
              >
                {displayName(file)}
              </p>
              <button
                type="button"
                onClick={startEdit}
                className="shrink-0 p-0.5 text-gray-400 hover:text-[#1977cc] rounded cursor-pointer"
                title="검색용 파일명 수정"
                aria-label="검색용 파일명 수정"
              >
                <i className="ri-pencil-line text-sm" />
              </button>
            </div>
            {!file.originalFilename?.trim() && (
              <p className="text-[10px] text-amber-600 mt-0.5 truncate" title={fallbackLabel(file)}>
                검색명 미설정 · {fallbackLabel(file)}
              </p>
            )}
            <p className="text-[10px] text-gray-400 mt-0.5">{formatBytes(file.sizeBytes)}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function MediaLibraryModal({
  open,
  onClose,
  onSelect,
  category = "image",
  title,
}: MediaLibraryModalProps) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const fetchPage = useCallback(
    async (page: number, size: number) => {
      const query = buildListQuery(page + 1, size, { category, search });
      const res = await api.getPage<PageResponse<ContentFile>>("/admin/files", query);
      return res.data;
    },
    [category, search],
  );

  const list = useServerPagination<ContentFile>({
    fetchPage,
    pageSize: GRID_PAGE_SIZE,
    pageSizeOptions: [12, 24, 48],
    resetDeps: [category, search],
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setSearch("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const modalTitle =
    title ?? (category === "pdf" ? "PDF 라이브러리" : "이미지 라이브러리");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleItemSelect = (file: ContentFile) => {
    onSelect(file.url, { originalFilename: file.originalFilename });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-[#2c4964]/40 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-library-title"
        className="w-full sm:max-w-4xl max-h-[92vh] flex flex-col bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl border border-gray-200/80 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-r from-[#2c4964] to-[#243a52] px-6 pt-6 pb-5 text-white shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="닫기"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <div className="flex items-center gap-3 pr-8">
            <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 border border-white/15 shrink-0">
              <i className={`text-xl ${category === "pdf" ? "ri-file-pdf-line" : "ri-image-line"}`} />
            </span>
            <div>
              <h2 id="media-library-title" className="text-lg font-bold tracking-tight">
                {modalTitle}
              </h2>
              <p className="text-white/60 text-xs mt-0.5">
                파일을 선택하거나 연필 아이콘으로 검색용 파일명을 설정할 수 있습니다
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSearch} className="px-4 sm:px-6 py-3 border-b border-gray-100 shrink-0">
          <div className="flex gap-2">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="검색용 파일명 또는 URL 검색"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20"
            />
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-[#1977cc] rounded-md hover:bg-[#1669b3] cursor-pointer"
            >
              검색
            </button>
          </div>
        </form>

        <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 py-4">
          {list.loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-400 gap-2">
              <div className="w-4 h-4 border-2 border-[#1977cc] border-t-transparent rounded-full animate-spin" />
              불러오는 중...
            </div>
          ) : list.items.length === 0 ? (
            <div className="text-center py-16 text-sm text-gray-400">
              {search
                ? "검색 결과가 없습니다."
                : `등록된 ${category === "pdf" ? "PDF" : "이미지"}가 없습니다. 먼저 업로드해 주세요.`}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {list.items.map((file) => (
                <MediaLibraryGridItem
                  key={file.id}
                  file={file}
                  onSelect={handleItemSelect}
                  onRenamed={list.reload}
                />
              ))}
            </div>
          )}
        </div>

        <Pagination
          page={list.page}
          totalPages={list.totalPages}
          totalItems={list.totalItems}
          pageSize={list.pageSize}
          rangeStart={list.rangeStart}
          rangeEnd={list.rangeEnd}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          pageSizeOptions={list.pageSizeOptions}
          className="shrink-0 rounded-none border-t border-gray-200"
        />
      </div>
    </div>
  );
}
